import { createApi } from "@reduxjs/toolkit/query/react";
import { createAuthBaseQuery } from "../auth/reauthBaseQuery";
import { appConfig } from "../../appConfig";

import {
  FilteredPagedModel,
  FilteredPagedQueryResult,
  EntityCommonInfoModel,
  CommonCollectorModel,
  CommonMergeModel,
  CommonMultiDeleteModel,
} from "../../entities/common";
import { CompanyIdentityModel } from "../../entities/companyIdentity";
import {
  ShareholderGroupModel,
  ShareholderModel,
  ShareholderSelectModel,
  CompanyShareholderModel,
  ShareholderEnterLeaveModel,
} from "../../entities/shareholder";
import { getQuerySearchParams } from "../../utils";

export const TagTypes = {
  CompanyIdentity: "CompanyIdentity",
  ShareholderGroup: "ShareholderGroup",
  Shareholder: "Shareholder",
  CompanyShareholder: "CompanyShareholder",
  BourseGeneralInfo: "BourseGeneralInfo",
  FaraBourseGeneralInfo: "FaraBourseGeneralInfo",
  Glance: "Glance",
  GlanceAdjustment: "GlanceAdjustment",
  GlanceAdjustmentMark: "GlanceAdjustmentMark",
  ShareholderEnterLeave: "ShareholderEnterLeave",
  ComputedPe: "ComputedPe",
};

export const boardSlice = createApi({
  reducerPath: "board",
  baseQuery: createAuthBaseQuery(`${appConfig.boardUrl}/api`),
  tagTypes: Object.values(TagTypes),

  endpoints: (builder) => ({
    // CompanyIdentity
    getCompanyIdentitiesCommonInfo: builder.query<EntityCommonInfoModel, undefined>({
      query: () => "/companyIdentity/commonInfo",
      providesTags: [{ type: TagTypes.CompanyIdentity, id: "LIST" }],
    }),
    getCompanyIdentitiesFilteredPaged: builder.query<FilteredPagedQueryResult<CompanyIdentityModel>, FilteredPagedModel>({
      query: ({ page = 1, pageSize = 10, searchTerm = "", sortFieldName = "", descending = false }: FilteredPagedModel) =>
        `/companyIdentity/pageable?page=${page}&pageSize=${pageSize}&searchTerm=${searchTerm}&sortFieldName=${sortFieldName}&descending=${descending}`,
      providesTags: (result, error, arg) =>
        result
          ? [
              { type: TagTypes.CompanyIdentity, id: "PARTIAL-LIST" },
              ...result.data.map(({ id }) => ({
                type: TagTypes.CompanyIdentity,
                id,
              })),
            ]
          : [{ type: TagTypes.CompanyIdentity, id: "PARTIAL-LIST" }],
    }),
    getCompanyIdentity: builder.query<CompanyIdentityModel, number>({
      query: (identityId) => `/companyIdentity/${identityId}`,
      providesTags: (result, error, arg) => [{ type: TagTypes.CompanyIdentity, id: arg }],
    }),
    collectCompanyIdentities: builder.mutation({
      query: (model: CommonCollectorModel) => ({
        url: "/companyIdentity/collect",
        method: "POST",
        body: model,
      }),
      invalidatesTags: [
        { type: TagTypes.CompanyIdentity, id: "LIST" },
        { type: TagTypes.CompanyIdentity, id: "PARTIAL-LIST" },
      ],
    }),
    deleteCompanyIdentity: builder.mutation({
      query: (identityId) => ({
        url: `/companyIdentity/${identityId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              { type: TagTypes.CompanyIdentity, id: arg },
              { type: TagTypes.CompanyIdentity, id: "LIST" },
            ],
    }),
    deleteMultiCompanyIdentities: builder.mutation({
      query: (multiDeleteModel: CommonMultiDeleteModel) => ({
        url: "/companyIdentity/multiDelete",
        method: "DELETE",
        body: multiDeleteModel,
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              ...arg.ids.map((id: number) => ({
                type: TagTypes.CompanyIdentity,
                id,
              })),
              { type: TagTypes.CompanyIdentity, id: "LIST" },
            ],
    }),

    // ShareholderGroup

    getShareholderGroupsCommonInfo: builder.query<EntityCommonInfoModel, undefined>({
      query: () => "/shareholderGroup/commonInfo",
      providesTags: [{ type: TagTypes.ShareholderGroup, id: "LIST" }],
    }),
    getShareholderGroupsFilteredPaged: builder.query<FilteredPagedQueryResult<ShareholderGroupModel>, FilteredPagedModel>({
      query: ({ page = 1, pageSize = 10, searchTerm = "", sortFieldName = "", descending = false }: FilteredPagedModel) =>
        `/shareholderGroup/pageable?page=${page}&pageSize=${pageSize}&searchTerm=${searchTerm}&sortFieldName=${sortFieldName}&descending=${descending}`,
      providesTags: (result, error, arg) =>
        result
          ? [
              { type: TagTypes.ShareholderGroup, id: "PARTIAL-LIST" },
              ...result.data.map(({ id }) => ({
                type: TagTypes.ShareholderGroup,
                id,
              })),
            ]
          : [{ type: TagTypes.ShareholderGroup, id: "PARTIAL-LIST" }],
    }),
    getShareholderGroupsSelectFilter: builder.query<ShareholderGroupModel[], string>({
      query: (filterText: string) => `/shareholderGroup/select/${filterText}`,
      providesTags: (data = [], error, arg) => [
        { type: TagTypes.ShareholderGroup, id: "PARTIAL-LIST" },
        ...data.map(({ id }: { id: number | string }) => ({
          type: TagTypes.ShareholderGroup,
          id,
        })),
      ],
    }),
    getShareholderGroup: builder.query<ShareholderGroupModel, number>({
      query: (groupId) => `/shareholderGroup/${groupId}`,
      providesTags: (result, error, arg) => [{ type: TagTypes.ShareholderGroup, id: arg }],
    }),
    addShareholderGroup: builder.mutation({
      query: (initialGroup) => ({
        url: "/shareholderGroup",
        method: "POST",
        body: initialGroup,
      }),
      invalidatesTags: [
        { type: TagTypes.ShareholderGroup, id: "LIST" },
        { type: TagTypes.ShareholderGroup, id: "PARTIAL-LIST" },
      ],
    }),
    editShareholderGroup: builder.mutation({
      query: (group) => ({
        url: `/shareholderGroup/${group.id}`,
        method: "PUT",
        body: group,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: TagTypes.ShareholderGroup, id: arg.id },
        { type: TagTypes.ShareholderGroup, id: "LIST" },
        { type: TagTypes.Shareholder, id: "LIST" },
        { type: TagTypes.Shareholder, id: "PARTIAL-LIST" },
      ],
    }),
    deleteShareholderGroup: builder.mutation({
      query: (groupId) => ({
        url: `/shareholderGroup/${groupId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              { type: TagTypes.ShareholderGroup, id: arg },
              { type: TagTypes.ShareholderGroup, id: "LIST" },
              { type: TagTypes.Shareholder, id: "LIST" },
              { type: TagTypes.Shareholder, id: "PARTIAL-LIST" },
            ],
    }),
    deleteMultiShareholderGroups: builder.mutation({
      query: (multiDeleteModel: CommonMultiDeleteModel) => ({
        url: "/shareholderGroup/multiDelete",
        method: "DELETE",
        body: multiDeleteModel,
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              ...arg.ids.map((id: number) => ({
                type: TagTypes.ShareholderGroup,
                id,
              })),
              { type: TagTypes.ShareholderGroup, id: "LIST" },
              { type: TagTypes.Shareholder, id: "LIST" },
              { type: TagTypes.Shareholder, id: "PARTIAL-LIST" },
            ],
    }),

    // Shareholder

    getShareholdersCommonInfo: builder.query<EntityCommonInfoModel, undefined>({
      query: () => "/shareholder/commonInfo",
      providesTags: [{ type: TagTypes.Shareholder, id: "LIST" }],
    }),
    getShareholdersFilteredPaged: builder.query<FilteredPagedQueryResult<ShareholderModel>, FilteredPagedModel>({
      query: ({ page = 1, pageSize = 10, searchTerm = "", sortFieldName = "", descending = false }: FilteredPagedModel) =>
        `/shareholder/pageable?page=${page}&pageSize=${pageSize}&searchTerm=${searchTerm}&sortFieldName=${sortFieldName}&descending=${descending}`,
      providesTags: (result, error, arg) =>
        result
          ? [
              { type: TagTypes.Shareholder, id: "PARTIAL-LIST" },
              ...result.data.map(({ id }) => ({
                type: TagTypes.Shareholder,
                id,
              })),
            ]
          : [{ type: TagTypes.Shareholder, id: "PARTIAL-LIST" }],
    }),
    getShareholdersSelectFilter: builder.query<ShareholderSelectModel[], string>({
      query: (filterText: string) => `/shareholder/select/${filterText}`,
      providesTags: (data = [], error, arg) => [
        { type: TagTypes.Shareholder, id: "PARTIAL-LIST" },
        ...data.map(({ id }: { id: number | string }) => ({
          type: TagTypes.Shareholder,
          id,
        })),
      ],
    }),
    getShareholder: builder.query<ShareholderModel, number>({
      query: (shareholderId) => `/shareholder/${shareholderId}`,
      providesTags: (result, error, arg) => [{ type: TagTypes.Shareholder, id: arg }],
    }),
    addShareholder: builder.mutation({
      query: (initialShareholder) => ({
        url: "/shareholder",
        method: "POST",
        body: initialShareholder,
      }),
      invalidatesTags: [
        { type: TagTypes.Shareholder, id: "LIST" },
        { type: TagTypes.Shareholder, id: "PARTIAL-LIST" },
      ],
    }),
    editShareholder: builder.mutation({
      query: (shareholder) => ({
        url: `/shareholder/${shareholder.id}`,
        method: "PUT",
        body: shareholder,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: TagTypes.Shareholder, id: arg.id },
        { type: TagTypes.Shareholder, id: "LIST" },
        { type: TagTypes.CompanyShareholder, id: "LIST" },
        { type: TagTypes.CompanyShareholder, id: "PARTIAL-LIST" },
      ],
    }),
    deleteShareholder: builder.mutation({
      query: (shareholderId) => ({
        url: `/shareholder/${shareholderId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              { type: TagTypes.Shareholder, id: arg },
              { type: TagTypes.Shareholder, id: "LIST" },
              { type: TagTypes.CompanyShareholder, id: "LIST" },
              { type: TagTypes.CompanyShareholder, id: "PARTIAL-LIST" },
            ],
    }),
    deleteMultiShareholders: builder.mutation({
      query: (multiDeleteModel: CommonMultiDeleteModel) => ({
        url: "/shareholder/multiDelete",
        method: "DELETE",
        body: multiDeleteModel,
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              ...arg.ids.map((id: number) => ({
                type: TagTypes.Shareholder,
                id,
              })),
              { type: TagTypes.Shareholder, id: "LIST" },
              { type: TagTypes.CompanyShareholder, id: "LIST" },
              { type: TagTypes.CompanyShareholder, id: "PARTIAL-LIST" },
            ],
    }),
    mergeShareholders: builder.mutation({
      query: (mergeModel: CommonMergeModel) => ({
        url: "/shareholder/merge",
        method: "POST",
        body: mergeModel,
      }),
      invalidatesTags: [
        { type: TagTypes.Shareholder, id: "LIST" },
        { type: TagTypes.Shareholder, id: "PARTIAL-LIST" },
        { type: TagTypes.CompanyShareholder, id: "LIST" },
        { type: TagTypes.CompanyShareholder, id: "PARTIAL-LIST" },
      ],
    }),

    // CompanyShareholder

    getCompanyShareholdersCommonInfo: builder.query<EntityCommonInfoModel, undefined>({
      query: () => "/companyShareholder/commonInfo",
      providesTags: [{ type: TagTypes.CompanyShareholder, id: "LIST" }],
    }),
    getCompanyShareholdersFilteredPaged: builder.query<FilteredPagedQueryResult<CompanyShareholderModel>, FilteredPagedModel>({
      query: ({ page = 1, pageSize = 10, searchTerm = "", sortFieldName = "", descending = false, ...rest }: FilteredPagedModel) => {
        let url = `/companyShareholder/pageable?page=${page}&pageSize=${pageSize}&searchTerm=${searchTerm}&sortFieldName=${sortFieldName}&descending=${descending}`;
        const restParams = getQuerySearchParams(rest);
        if (restParams) {
          url += `&${restParams}`;
        }
        return url;
      },
      providesTags: (result, error, arg) =>
        result
          ? [
              { type: TagTypes.CompanyShareholder, id: "PARTIAL-LIST" },
              ...result.data.map(({ id }) => ({
                type: TagTypes.CompanyShareholder,
                id,
              })),
            ]
          : [{ type: TagTypes.CompanyShareholder, id: "PARTIAL-LIST" }],
    }),
    getCompanyShareholder: builder.query<CompanyShareholderModel, number>({
      query: (companyShareholderId) => `/companyShareholder/${companyShareholderId}`,
      providesTags: (result, error, arg) => [{ type: TagTypes.CompanyShareholder, id: arg }],
    }),
    collectCompanyShareholders: builder.mutation({
      query: (model: CommonCollectorModel) => ({
        url: "/companyShareholder/collect",
        method: "POST",
        body: model,
      }),
      invalidatesTags: [
        { type: TagTypes.CompanyShareholder, id: "LIST" },
        { type: TagTypes.CompanyShareholder, id: "PARTIAL-LIST" },
        { type: TagTypes.Shareholder, id: "LIST" },
        { type: TagTypes.Shareholder, id: "PARTIAL-LIST" },
      ],
    }),
    deleteCompanyShareholder: builder.mutation({
      query: (companyShareholderId) => ({
        url: `/companyShareholder/${companyShareholderId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              { type: TagTypes.CompanyShareholder, id: arg },
              { type: TagTypes.CompanyShareholder, id: "LIST" },
            ],
    }),
    deleteMultiCompanyShareholders: builder.mutation({
      query: (multiDeleteModel: CommonMultiDeleteModel) => ({
        url: "/companyShareholder/multiDelete",
        method: "DELETE",
        body: multiDeleteModel,
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              ...arg.ids.map((id: number) => ({
                type: TagTypes.CompanyShareholder,
                id,
              })),
              { type: TagTypes.CompanyShareholder, id: "LIST" },
            ],
    }),

    // ShareholderEnterLeave

    getShareholdersEnterLeaveCommonInfo: builder.query<EntityCommonInfoModel, undefined>({
      query: () => "/companyShareholder/enterLeave/commonInfo",
      providesTags: [{ type: TagTypes.ShareholderEnterLeave, id: "LIST" }],
    }),
    getShareholdersEnterLeaveFilteredPaged: builder.query<FilteredPagedQueryResult<ShareholderEnterLeaveModel>, FilteredPagedModel>({
      query: ({ page = 1, pageSize = 10, searchTerm = "", sortFieldName = "", descending = false, ...rest }: FilteredPagedModel) => {
        let url = `/companyShareholder/enterLeave/pageable?page=${page}&pageSize=${pageSize}&searchTerm=${searchTerm}&sortFieldName=${sortFieldName}&descending=${descending}`;
        const restParams = getQuerySearchParams(rest);
        if (restParams) {
          url += `&${restParams}`;
        }
        return url;
      },
      providesTags: (result, error, arg) =>
        result
          ? [
              { type: TagTypes.ShareholderEnterLeave, id: "PARTIAL-LIST" },
              ...result.data.map(({ id }) => ({
                type: TagTypes.ShareholderEnterLeave,
                id,
              })),
            ]
          : [{ type: TagTypes.ShareholderEnterLeave, id: "PARTIAL-LIST" }],
    }),
    getShareholderEnterLeave: builder.query<ShareholderEnterLeaveModel, number>({
      query: (shareholderEnterLeaveId) => `/companyShareholder/enterLeave/${shareholderEnterLeaveId}`,
      providesTags: (result, error, arg) => [{ type: TagTypes.CompanyShareholder, id: arg }],
    }),
    collectShareholdersEnterLeave: builder.mutation({
      query: (model: { symbols: string[]; full: boolean }) => ({
        url: "/companyShareholder/enterLeave/collect",
        method: "POST",
        body: model,
      }),
      invalidatesTags: [
        { type: TagTypes.ShareholderEnterLeave, id: "LIST" },
        { type: TagTypes.ShareholderEnterLeave, id: "PARTIAL-LIST" },
      ],
    }),
    deleteShareholderEnterLeave: builder.mutation({
      query: (shareholderEnterLeaveId) => ({
        url: `/companyShareholder/enterLeave/${shareholderEnterLeaveId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              { type: TagTypes.ShareholderEnterLeave, id: arg },
              { type: TagTypes.ShareholderEnterLeave, id: "LIST" },
            ],
    }),
    deleteMultiShareholdersEnterLeave: builder.mutation({
      query: (multiDeleteModel: CommonMultiDeleteModel) => ({
        url: "/companyShareholder/enterLeave/multiDelete",
        method: "DELETE",
        body: multiDeleteModel,
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              ...arg.ids.map((id: number) => ({
                type: TagTypes.ShareholderEnterLeave,
                id,
              })),
              { type: TagTypes.ShareholderEnterLeave, id: "LIST" },
            ],
    }),
  }),
});

export const {
  useGetCompanyIdentitiesCommonInfoQuery,
  useGetCompanyIdentitiesFilteredPagedQuery,
  useGetCompanyIdentityQuery,
  useCollectCompanyIdentitiesMutation,
  useDeleteCompanyIdentityMutation,
  useDeleteMultiCompanyIdentitiesMutation,

  useGetShareholderGroupsFilteredPagedQuery,
  useGetShareholderGroupQuery,
  useGetShareholderGroupsCommonInfoQuery,
  useGetShareholderGroupsSelectFilterQuery,
  useAddShareholderGroupMutation,
  useEditShareholderGroupMutation,
  useDeleteShareholderGroupMutation,
  useDeleteMultiShareholderGroupsMutation,

  useGetShareholderQuery,
  useGetShareholdersCommonInfoQuery,
  useGetShareholdersFilteredPagedQuery,
  useGetShareholdersSelectFilterQuery,
  useAddShareholderMutation,
  useEditShareholderMutation,
  useDeleteShareholderMutation,
  useMergeShareholdersMutation,
  useDeleteMultiShareholdersMutation,

  useGetCompanyShareholderQuery,
  useGetCompanyShareholdersCommonInfoQuery,
  useGetCompanyShareholdersFilteredPagedQuery,
  useCollectCompanyShareholdersMutation,
  useDeleteCompanyShareholderMutation,
  useDeleteMultiCompanyShareholdersMutation,

  useGetShareholderEnterLeaveQuery,
  useGetShareholdersEnterLeaveCommonInfoQuery,
  useGetShareholdersEnterLeaveFilteredPagedQuery,
  useCollectShareholdersEnterLeaveMutation,
  useDeleteShareholderEnterLeaveMutation,
  useDeleteMultiShareholdersEnterLeaveMutation,
} = boardSlice;
